import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import type { ControllerListRow } from '@/features/controller/hooks/bulkActions/useControllerListRows'
import { useControllerListRows } from '@/features/controller/hooks/bulkActions/useControllerListRows'
import {
  parseControllerListIdentifiers,
  splitControllerListText,
} from '@/features/controller/utils/parseControllerListIdentifiers'

export type UseControllerListResult = ReturnType<typeof useControllerList>

/** The controllers listed in the `ids` search param, fetched, and which of them are selected */
export function useControllerList() {
  const [searchParams, setSearchParams] = useSearchParams()
  const idsParam = searchParams.get('ids') ?? ''

  const { validIdentifiers: controllerListIdentifiers, invalidIdentifiers } = useMemo(
    () => parseControllerListIdentifiers(idsParam),
    [idsParam],
  )
  const rows = useControllerListRows(controllerListIdentifiers)

  // The list the selection was made for, so that it can be adjusted when the list changes
  const [selection, setSelection] = useState<{ idsParam: string; rawValues: ReadonlySet<string> }>({
    idsParam,
    rawValues: new Set(),
  })
  if (selection.idsParam !== idsParam) {
    // However the list changed, also by back/forward or through an empty list that has no grid,
    // a controller that left it doesn't come back selected if it is added again
    const current = new Set(controllerListIdentifiers.map((identifier) => identifier.rawValue))
    setSelection({ idsParam, rawValues: new Set([...selection.rawValues].filter((rawValue) => current.has(rawValue))) })
  }
  const selectedRawValues = selection.rawValues

  function setSelectedRawValues(rawValues: ReadonlySet<string>) {
    setSelection({ idsParam, rawValues })
  }

  // Once per controller, also when it was entered both by its equipment number and by its id
  const selectedControllers = [
    ...new Map(
      rows.flatMap(({ identifier, query }) =>
        query.data && selectedRawValues.has(identifier.rawValue) ? [[query.data.id, query.data] as const] : [],
      ),
    ).values(),
  ]

  function setIdsParam(entries: string[]) {
    const uniqueEntries = [...new Set(entries)]
    setSearchParams(uniqueEntries.length > 0 ? { ids: uniqueEntries.join(' ') } : {})
  }

  function setListText(text: string) {
    setIdsParam(splitControllerListText(text))
  }

  function removeController(row: ControllerListRow) {
    setIdsParam(splitControllerListText(idsParam).filter((entry) => entry !== row.identifier.rawValue))
  }

  function removeInvalidEntries() {
    setIdsParam(controllerListIdentifiers.map((identifier) => identifier.rawValue))
  }

  return {
    listText: idsParam,
    invalidIdentifiers,
    rows,
    selectedControllers,
    selectedRawValues,
    setSelectedRawValues,
    setListText,
    removeController,
    removeInvalidEntries,
  }
}
